import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ImageViewer } from 'antd-mobile';
import Dialog from '../../../../../../lib/Dialog';
import { useStateContext } from '@xkit-yx/im-kit-ui';
import './css/MediaSearch.css';

// ── 并发加载队列（模块级单例，不随组件重渲染重建）─────────────────────────────
const MAX_CONCURRENT = 4;
let _activeLoads = 0;
const _loadQueue = [];

function _flushQueue() {
  while (_activeLoads < MAX_CONCURRENT && _loadQueue.length > 0) {
    const task = _loadQueue.shift();
    _activeLoads++;
    task(() => { _activeLoads--; _flushQueue(); });
  }
}

function enqueueLoad(task) {
  _loadQueue.push(task);
  _flushQueue();
}

// ── 单个媒体格子（含并发懒加载逻辑）─────────────────────────────────────────
const LazyMediaCell = React.memo(function LazyMediaCell({ msg, onImageClick, onVideoClick }) {
  const isVideo  = msg.type === 'video';
  // 历史消息封面可能在 file.cover 或 attach.cover/thumb 下
  const thumbSrc = isVideo
    ? (msg.file?.cover || msg.attach?.cover || msg.attach?.thumb || null)
    : (msg.attach?.url || null);
  // 无封面时用 #t=0.001 trick 让浏览器渲染第一帧
  const videoUrl = isVideo ? (msg.file?.url || msg.attach?.url || '') : '';

  const [displaySrc, setDisplaySrc] = useState(null);
  const [showVideo, setShowVideo]   = useState(false); // 无封面时延迟渲染 video 元素
  const containerRef = useRef(null);
  const enqueuedRef  = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || enqueuedRef.current) return;
      enqueuedRef.current = true;
      observer.disconnect();

      if (thumbSrc) {
        // 有封面 URL：通过并发队列懒加载图片
        enqueueLoad((done) => {
          const img = new Image();
          img.onload  = () => { setDisplaySrc(thumbSrc); done(); };
          img.onerror = () => { setDisplaySrc(thumbSrc); done(); };
          img.src = thumbSrc;
        });
      } else if (isVideo && videoUrl) {
        // 无封面：进入视口后渲染 video 元素，浏览器会自动捕获第一帧
        setShowVideo(true);
      }
    }, { rootMargin: '200px 0px' });

    observer.observe(el);
    return () => observer.disconnect();
  // eslint-disable-next-line
  }, [thumbSrc, isVideo, videoUrl]);

  return (
    <div ref={containerRef} className="media-cell">
      <div
        className="media-cell-inner"
        onClick={() => isVideo ? onVideoClick(msg) : onImageClick(msg)}
      >
        {displaySrc ? (
          <img src={displaySrc} alt="" decoding="async" />
        ) : showVideo && videoUrl ? (
          // 用 #t=0.001 让浏览器 seek 到第一帧作为封面
          <video
            src={videoUrl.includes('#') ? videoUrl : `${videoUrl}#t=0.001`}
            preload="metadata"
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div className={isVideo ? 'media-cell-no-thumb' : 'media-cell-skeleton'} />
        )}
        {isVideo && (
          <div className="media-cell-video-icon">
            <svg viewBox="0 0 24 24" width="28" height="28">
              <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.45)" />
              <polygon points="9.5,7 18,12 9.5,17" fill="white" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
});

function groupByDate(messages) {
  const groups = {};
  messages.forEach(msg => {
    const d = new Date(msg.time);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

// 全屏视频播放浮层
function VideoOverlay({ src, onClose }) {
  const videoRef = useRef(null);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  return (
    <>
      <div className="media-video-overlay" onClick={onClose}>
        <video
          ref={videoRef}
          src={src}
          className="media-video-fullscreen"
          controls
          playsInline
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="media-video-close" onClick={onClose}>✕</div>
    </>
  );
}

export default function MediaSearch(props) {
  const { tid } = props.query;
  const { nim } = useStateContext();
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const lastMsgIdRef   = useRef(undefined); // 上次最后一条消息的 idServer
  const lastMsgTimeRef = useRef(undefined); // 上次最后一条消息的 time（endTime 游标）
  const loadingRef   = useRef(false);
  const allLoadedRef = useRef(false);
  const sentinelRef  = useRef(null);

  useEffect(() => { loadingRef.current  = loading;   }, [loading]);
  useEffect(() => { allLoadedRef.current = allLoaded; }, [allLoaded]);

  const loadMedia = useCallback(async (loadMore = false) => {
    if (loadingRef.current) return;
    if (allLoadedRef.current) return;
    setLoading(true);
    if (!loadMore) {
      Dialog.loading();
      // 重新加载时重置游标
      lastMsgIdRef.current   = undefined;
      lastMsgTimeRef.current = undefined;
    }

    const BATCH    = 20;  // 单次请求条数
    const TARGET   = 20;  // 每次 loadMore 至少凑够 20 条媒体再渲染
    const MAX_ITER = 20;  // 安全上限，防止极端情况死循环
    const collected = [];
    let iter = 0;

    try {
      // 循环拉取，直到凑够媒体条目或消息耗尽
      // 终止条件：返回空数组（而非 < limit），SDK 实际每批条数可能小于 limit
      while (collected.length < TARGET && iter < MAX_ITER) {
        iter++;
        const raw = await nim.getHistoryMsgs({
          scene:     'team',
          to:        tid,
          msgTypes:  ['image', 'video'], // SDK 支持，直接过滤减少流量
          limit:     BATCH,
          reverse:   false,
          endTime:   lastMsgTimeRef.current,
          lastMsgId: lastMsgIdRef.current,
        });

        const batch = raw || [];
        if (batch.length === 0) {
          // 返回空 → 真正到头了
          setAllLoaded(true);
          allLoadedRef.current = true;
          break;
        }

        // 更新游标到本批最旧的一条（time 最小）
        const oldest = batch.reduce((a, b) => a.time < b.time ? a : b);
        lastMsgIdRef.current   = oldest.idServer;
        lastMsgTimeRef.current = oldest.time;

        // 客户端二次过滤（兜底，防止 SDK 返回非媒体消息）
        const media = batch.filter(m => m.type === 'image' || m.type === 'video');
        collected.push(...media);

        // 只有返回空才停，不依赖 batch.length < BATCH
      }

      if (collected.length > 0) {
        setMediaList(prev => loadMore ? [...prev, ...collected] : collected);
      }
    } catch (err) {
      Dialog.error(err.message || '加载失败');
    } finally {
      setLoading(false);
      if (!loadMore) Dialog.close();
    }
  // eslint-disable-next-line
  }, [nim, tid]);

  useEffect(() => {
    if (nim && props.active) loadMedia();
  // eslint-disable-next-line
  }, [nim, props.active]);

  // 底部哨兵：进入视口时自动加载更多
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingRef.current && !allLoadedRef.current) {
        loadMedia(true);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [mediaList.length, loadMedia]);

  // 所有图片 URL 的平铺列表，用于多图预览左右滑
  const allImageUrls = mediaList
    .filter(msg => msg.type === 'image')
    .map(msg => msg.attach?.url || '');

  const handleImageClick = (msg) => {
    const url = msg.attach?.url || '';
    const index = allImageUrls.indexOf(url);
    ImageViewer.Multi.show({
      images: allImageUrls,
      defaultIndex: index >= 0 ? index : 0,
    });
  };

  const handleVideoClick = (msg) => {
    setVideoSrc(msg.attach?.url || '');
  };

  const groups = groupByDate(mediaList);

  return (
    <div className="media-search">
      {loading && mediaList.length === 0 && (
        <div className="media-search-loading"></div>
      )}
      {!loading && mediaList.length === 0 && (
        <div className="media-search-empty">暂无图片/视频记录</div>
      )}

      {groups.map(([date, msgs]) => (
        <div key={date} className="media-group">
          <div className="media-group-date">{date}</div>
          <div className="media-grid">
            {msgs.map((msg, idx) => (
              <LazyMediaCell
                key={msg.idClient || idx}
                msg={msg}
                onImageClick={handleImageClick}
                onVideoClick={handleVideoClick}
              />
            ))}
          </div>
        </div>
      ))}

      {mediaList.length > 0 && !allLoaded && (
        <div ref={sentinelRef} className="media-load-more-sentinel">
          {loading && <div className="media-load-more-spinner" />}
        </div>
      )}
      {allLoaded && mediaList.length > 0 && (
        <div className="media-load-more-done">全部加载完毕</div>
      )}

      {videoSrc && (
        <VideoOverlay src={videoSrc} onClose={() => setVideoSrc(null)} />
      )}
    </div>
  );
}
